/**
 * Test automatique du QR code d'une carte avant impression (spec § 17).
 *
 * On encode l'URL avec EXACTEMENT les mêmes réglages que l'image imprimée
 * (`CARD_QR_OPTIONS`, lib `qrcode`), on rastérise la matrice de modules en
 * pixels (zone calme comprise) puis on la DÉCODE avec `jsqr`, un décodeur
 * indépendant. Le bouton d'impression n'est activé que si le décodage redonne
 * l'URL attendue à l'identique. Pur (sans canvas) → testable en Node.
 */

import QRCode from 'qrcode';
import jsQR from 'jsqr';
import { CARD_QR_OPTIONS } from '@jeraisonne/db/cards';

/** Réglages du QR imprimé — source unique dans `@jeraisonne/db/cards` (image + vérification). */
export { CARD_QR_OPTIONS };

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/**
 * Rastérise une matrice de modules QR en RGBA, zone calme incluse. `color` =
 * couleurs du module (`dark`) et du fond (`light`), noir sur blanc par défaut.
 */
export function rasterizeQr(
  modules: { size: number; get: (row: number, col: number) => number | boolean },
  margin: number,
  scale: number,
  color: { dark: string; light: string } = { dark: '#000000', light: '#ffffff' },
): { data: Uint8ClampedArray; width: number; height: number } {
  const dim = (modules.size + margin * 2) * scale;
  const [fr, fg, fb] = hexToRgb(color.dark);
  const [br, bg, bb] = hexToRgb(color.light);
  const data = new Uint8ClampedArray(dim * dim * 4);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = br;
    data[i + 1] = bg;
    data[i + 2] = bb;
    data[i + 3] = 255;
  }
  for (let r = 0; r < modules.size; r++) {
    for (let c = 0; c < modules.size; c++) {
      if (!modules.get(r, c)) continue;
      for (let dy = 0; dy < scale; dy++) {
        for (let dx = 0; dx < scale; dx++) {
          const y = (r + margin) * scale + dy;
          const x = (c + margin) * scale + dx;
          const i = (y * dim + x) * 4;
          data[i] = fr;
          data[i + 1] = fg;
          data[i + 2] = fb;
        }
      }
    }
  }
  return { data, width: dim, height: dim };
}

export interface QrCheckResult {
  ok: boolean;
  decoded: string | null;
  /** Nombre de modules par côté (hors zone calme) — indicateur de densité. */
  modules: number;
}

export function checkCardQr(expectedUrl: string): QrCheckResult {
  const qr = QRCode.create(expectedUrl, {
    errorCorrectionLevel: CARD_QR_OPTIONS.errorCorrectionLevel,
  });
  const img = rasterizeQr(qr.modules, CARD_QR_OPTIONS.margin, 6, CARD_QR_OPTIONS.color);
  // QR standard (sombre sur clair) : décodé SANS inversion, comme tout lecteur.
  const decoded =
    jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' })?.data ?? null;
  return { ok: decoded === expectedUrl, decoded, modules: qr.modules.size };
}
